// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `viewport-frozen-layers` route body (PR-C, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `viewport-frozen-layers` nested drill-down: the per-VIEWPORT frozen layers, `?viewportEntityId`
// REQUIRED. The seam already enforced the requirement (absent → `missing-viewport-id`) and the
// existence (non-VIEWPORT id → `unknown-viewport-id`) — so a body that runs here has a present
// `?viewportEntityId` resolving to a VIEWPORT entity. The body FILTERS `snapshot.viewports` to that ONE
// viewport, projects its frozen layers, PAGINATED / SORTED / FIELD-projected.
//
// TWO PARALLEL UNRECONCILED REPRESENTATIONS — BOTH exposed RAW (ПРИОРИТЕТ #1 — no silent DB truncation):
// a VIEWPORT carries its frozen layers in TWO independent forms (model.ts:1397/:1520) that the model
// DELIBERATELY keeps UNRECONCILED (model.ts:1517-1519 — resolving them is the separately-deferred
// `S-VIEWPORT-OVERRIDE-RESOLVE` slice):
//   • `frozenLayers` — the proprietary EDIT_DWG_VP_OVERRIDES XDATA layer NAMES (the editor's in-memory truth).
//   • `frozenLayerHandles` — the native DXF group-331 soft-pointer HANDLES (the file's authoritative native
//     freeze representation).
// A viewport imported with native 331 handles but WITHOUT the XDATA names has `frozenLayers` EMPTY — so a
// names-only projection would make those frozen layers INVISIBLE via `state.read` (the @codex P2). Each item
// is therefore a DISCRIMINATED shape `{ layerName?, handle? }` carrying EXACTLY ONE raw form: one item per
// `frozenLayers` name (with `layerName`), then one item per `frozenLayerHandles` handle (with `handle`). NO
// name↔handle pairing is FABRICATED (the lists are parallel + unreconciled — inferring a pairing would be a
// fake field; the handle→name RESOLVE is the deferred slice). Order: names first (in list order) then handles
// (in list order), stable. `total` = `frozenLayers.length + frozenLayerHandles.length`.
//
// S7 reads BOTH `frozenLayers` and `frozenLayerHandles` OFF the entity VERBATIM (store-free, NO reconcile
// here) and populates the facet; the projector projects the PRE-CAPTURED lists.
//
// routeRevisions: `{ geometryRevision, tableRevision }` — a per-VP VPLAYER freeze writes the
// `ViewportEntity.frozenLayers` entity-state (geometryRevision) and references the layer table
// (tableRevision). `snapshotRevision` rides as the page ANCHOR only (INV-9).
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the shared helpers. depcruise cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import {
  type RouteProjector,
  type SnapshotViewportRef,
} from "../sceneSummaryProjector";
import { compareFacetsBy, finishNestedViewportPage, parseKeyDirSort } from "./projectionHelpers";

/** The LEAN CORE (and only) field set: the two PARALLEL raw forms (a row carries exactly one). */
const LEAN_CORE_FIELDS = ["layerName", "handle"] as const;

/** The legal `key:dir` sort KEYS: by the XDATA layer NAME, or by the native 331 HANDLE. */
const FROZEN_SORT_KEYS = ["layerName", "handle"] as const;
type FrozenSortKey = (typeof FROZEN_SORT_KEYS)[number];

/**
 * One projected frozen-layer row — a DISCRIMINATED carrier of EXACTLY ONE raw representation: either the
 * XDATA layer NAME (`layerName`) OR the native group-331 HANDLE (`handle`), NEVER both (the two lists are
 * PARALLEL + UNRECONCILED — pairing them would fabricate a name↔handle mapping the deferred RESOLVE slice
 * owns). `projectFacetFields` omits the absent member (INV-1, an absent optional is not emitted).
 */
interface FrozenLayerRow {
  readonly layerName?: string;
  readonly handle?: string;
}

/** Extract a frozen-layer row's sort value (`layerName` or `handle` — each a string, or undefined when the row carries the other form). */
function frozenSortValue(row: FrozenLayerRow, key: FrozenSortKey): string | number | undefined {
  switch (key) {
    case "layerName":
      return row.layerName;
    case "handle":
      return row.handle;
    default:
      return undefined;
  }
}

/**
 * Resolve the ONE target viewport the `?viewportEntityId` names from the snapshot's viewport facet.
 * The seam already validated the id resolves to a VIEWPORT entity; this finds the matching RICH facet
 * row. `undefined` only if the facet and the `entities` view disagree (defensive — yields an empty page).
 */
export function findTargetViewport(
  viewports: readonly SnapshotViewportRef[],
  viewportEntityId: string | undefined,
): SnapshotViewportRef | undefined {
  if (viewportEntityId === undefined || viewportEntityId.length === 0) {
    return undefined; // unreachable post-validation (the seam rejected an absent id) — defensive.
  }
  return viewports.find((v) => v.id === viewportEntityId);
}

/**
 * Build the combined frozen-layer rows from a viewport's TWO parallel raw lists: one `{ layerName }` per
 * XDATA name (in list order) FOLLOWED BY one `{ handle }` per native group-331 handle (in list order). The
 * two are concatenated WITHOUT inferring any name↔handle correspondence (UNRECONCILED — ПРИОРИТЕТ #1). A
 * defensive `undefined` target ⇒ both lists empty ⇒ an empty rows array. `frozenLayerHandles` is OPTIONAL on
 * the schema (absent ⇒ no native 331 freeze ⇒ no handle rows).
 */
function frozenLayerRows(target: SnapshotViewportRef | undefined): FrozenLayerRow[] {
  if (target === undefined) {
    return [];
  }
  const nameRows: FrozenLayerRow[] = target.frozenLayers.map((layerName) => ({ layerName }));
  const handleRows: FrozenLayerRow[] = (target.frozenLayerHandles ?? []).map((handle) => ({ handle }));
  return [...nameRows, ...handleRows]; // names FIRST (in order) then handles (in order) — stable.
}

/**
 * The `viewport-frozen-layers` route body. resolve target VP → project BOTH its parallel raw freeze
 * lists (XDATA names + native group-331 handles, names first) → sort → (fullField | field-project) →
 * paginate. Binds `routeRevisions` to `{ geometryRevision, tableRevision }`. `snapshotRevision` rides as
 * the page ANCHOR only. `resolvedSpaceId: null` (a nested drill-down is keyed by the viewport, not a
 * space). byteTruncated: false. NO name↔handle pairing is fabricated (the RESOLVE slice is deferred).
 */
export const projectViewportFrozenLayers: RouteProjector = ({ query, cursor, snapshot }) => {
  // RESOLVE the ONE viewport `?viewportEntityId` names (the seam validated it resolves). A defensive
  // miss (the facet lacks a row the `entities` view advertised) → both lists empty (empty page). BOTH
  // parallel representations are projected RAW: the XDATA names then the native group-331 handles.
  const target = findTargetViewport(snapshot.viewports, query.viewportEntityId);
  const rows = frozenLayerRows(target);

  // SORT (before pagination — cursor-determinism). No `?sort` → the combined list order (names first,
  // then handles — round-trip preserved within each list). `layerName`/`handle` are strings; a row
  // carries exactly one, so a `layerName:asc` sort orders the handle rows (undefined name) first
  // (deterministic — `compareValues` orders undefined before present), and vice versa for `handle:asc`.
  const parsedSort = parseKeyDirSort<FrozenSortKey>(query.sort, FROZEN_SORT_KEYS);
  const ordered =
    parsedSort === null
      ? rows
      : [...rows].sort((a, b) => compareFacetsBy(a, b, parsedSort.key, parsedSort.dir, frozenSortValue));

  // PROJECTION-MODE + PAGINATION + FIELD-PROJECTION + ENVELOPE — the SHARED NESTED-route tail. Both nested
  // viewport routes bind `{ geometryRevision, tableRevision }` + `resolvedSpaceId: null` (a per-VP freeze is
  // ViewportEntity entity-state referencing the layer table, fullstack:965; keyed by the viewport, not a
  // space). `snapshotRevision` rides as the page ANCHOR only.
  return finishNestedViewportPage(ordered, query, cursor, LEAN_CORE_FIELDS, snapshot);
};
